import { PeriodsResponse } from '@/app/models/period';
import { usePeriodsQuery, useProcessMutation, useYearsQuery } from '@/app/services/periodApi';
import { useSyncEmployeeMutation, useSyncSharingMutation } from '@/app/services/syncSharingApi';
import LateralDrawer from '@/components/common/LateralDrawer';
import PageTitle from '@/components/common/pageTitle';
import NewPeriodRequestScreen from '@/components/period/NewPeriodRequestScreen';
import NewSendReason from '@/components/period/NewSendReason';
import NewSendTranspSpreadsheet from '@/components/period/NewSendTranspSpreadsheet';
import PeriodTable from '@/components/period/periodTables';
import { useYear } from '@/context/yearsContext';
import { Box, Button, FormControl, MenuItem, Select, Stack, Typography, useTheme } from '@mui/material';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';

// Enum to manage drawers
export enum DrawerType {
  NONE = '',
  SEND_ATTACHMENT = 'SEND_ATTACHMENT',
  SEND_REASON = 'SEND_REASON',
  SEND_TRANSP_SPREADSHEET = 'SEND_TRANSP_SPREADSHEET',
  SYNC_EMPLOYEES = 'SYNC_EMPLOYEES',
  SYNC_SHARING = 'SYNC_SHARING',
}

function Imports() {
  const { t } = useTranslation('periods');
  const theme = useTheme();

  const [openDrawer, setOpenDrawer] = useState<DrawerType>(DrawerType.NONE);
  const [selectedPeriod, setSelectedPeriod] = useState<PeriodsResponse | null>(null);
  const { selectedYear, setSelectedYear } = useYear();

  const { data: years, isSuccess } = useYearsQuery();
  const { data, refetch } = usePeriodsQuery(
    { date: selectedYear as string },
    {
      skip: !selectedYear,
    },
  );

  const [isDisabled, setIsDisabled] = useState(false);

  useEffect(() => {
    if (isSuccess && years) {
      setSelectedYear(years[0]);
    }
  }, [years, setSelectedYear, isSuccess]);

  const handleOpenDrawer = (drawer: DrawerType) => {
    setOpenDrawer(drawer);
  };
  const handleCloseDrawer = () => setOpenDrawer(DrawerType.NONE);

  const [syncSharing] = useSyncSharingMutation();
  const handleSyncSharing = async (date: string) => {
    handleCloseDrawer();
    setIsDisabled(true);
    try {
      const initialData = data?.find((p) => p.date === date);
      const initialLastImportDate = initialData?.shared?.lastImportDate;

      await toast.promise(
        syncSharing(date).unwrap(),
        {
          pending: t('common:processing.wait.a.moment') as string,
          success: t('sharing.sent.successfully') as string,
          error: t('common:processing.failed') as string,
        },
        { position: toast.POSITION.TOP_RIGHT },
      );

      let refetches = 0;
      const maxRefetches = 5;

      const interval = setInterval(async () => {
        const result = await refetch();
        const updatedPeriod = result?.data?.find((p) => p.date === date);
        if (updatedPeriod?.shared?.lastImportDate !== initialLastImportDate) {
          clearInterval(interval);
          toast.success(t('synchronized.distribution.successfully') as string, {
            position: toast.POSITION.TOP_RIGHT,
          });
          return;
        }

        refetches += 1;
        if (refetches >= maxRefetches) {
          clearInterval(interval);
        }
      }, 5000);
    } catch (error) {
      toast.error(t('common:processing.failed') as string, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
    setIsDisabled(false);
  };

  const [syncEmployee] = useSyncEmployeeMutation();

  const handleSyncEmployee = async (date: string) => {
    handleCloseDrawer();
    setIsDisabled(true);
    try {
      const initialData = data?.find((p) => p.date === date);
      const initialLastImportDate = initialData?.employees?.lastImportDate;

      await toast.promise(
        syncEmployee(date).unwrap(),
        {
          pending: t('common:processing.wait.a.moment') as string,
          success: t('employee.sharing.sent.successfully') as string,
          error: t('common:processing.failed') as string,
        },
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );

      let refetches = 0;
      const maxRefetches = 5;

      const interval = setInterval(async () => {
        const result = await refetch();
        const updatedPeriod = result?.data?.find((p) => p.date === date);
        if (updatedPeriod?.shared?.lastImportDate !== initialLastImportDate) {
          clearInterval(interval);
          toast.success(t('collaborators.successfully.synchronized') as string, {
            position: toast.POSITION.TOP_RIGHT,
          });
          return;
        }

        refetches += 1;
        if (refetches >= maxRefetches) {
          clearInterval(interval);
        }
      }, 5000);
    } catch (error) {
      toast.error(t('common:processing.failed') as string, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
    setIsDisabled(false);
  };
  const [process] = useProcessMutation();
  const handleProcess = async (date: string) => {
    handleCloseDrawer();
    setIsDisabled(true);

    try {
      // valor atual
      const initial = data?.find((p) => p.processedDate === date);
      const initialProcessedDate = initial?.processedDate ?? null;

      await toast.promise(
        process(date).unwrap(),
        {
          pending: t('common:processing.wait.a.moment') as string,
          success: t('sent.for.processing') as string,
          error: t('common:processing.failed') as string,
        },
        {
          position: toast.POSITION.TOP_RIGHT,
        },
      );

      let refetches = 0;
      const maxRefetches = 5;

      const interval = setInterval(async () => {
        const result = await refetch();
        const updatedData = result?.data ?? [];

        // First, I locate the updated period.
        const updatedPeriod = updatedData.find((p) => p.processedDate === date);

        // I'll check later if the processing date has changed.
        const hasUpdated = updatedPeriod?.processedDate && updatedPeriod.processedDate !== initialProcessedDate;

        if (hasUpdated) {
          clearInterval(interval);
          toast.success(t('successfully.synchronized') as string, {
            position: toast.POSITION.TOP_RIGHT,
          });
          return;
        }

        refetches += 1;
        if (refetches >= maxRefetches) {
          clearInterval(interval);
        }
      }, 5000);

      // Perform an initial refetch outside the loop.
      refetch();
    } catch (error) {
      toast.error(t('common:processing.failed') as string, {
        position: toast.POSITION.TOP_RIGHT,
      });
    }
    setIsDisabled(false);
  };

  return (
    <Box
      sx={{
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        width: '100%',
        marginRight: '15px',
      }}
    >
      <PageTitle title={t('periods')} />

      <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ minWidth: theme.spacing(31) }}>
          <Typography component="p" variant="fieldHeader">
            {t('year')}
          </Typography>
          <FormControl sx={{ width: theme.spacing(30) }}>
            <Select
              displayEmpty
              value={selectedYear || ({} as string)}
              onChange={(event) => {
                if (event.target.value) {
                  setSelectedYear(event.target.value);
                }
              }}
              sx={{ height: 32, fontSize: 14 }}
            >
              {years?.map((year: string) => (
                <MenuItem key={year} value={year} sx={{ fontSize: 13 }}>
                  {year}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      </Box>

      <Box sx={{ display: 'flex', textAlign: 'center', justifyContent: 'right', mb: 4 }}>
        <Stack direction="row" alignItems="center" sx={{ ml: 2 }}>
          <Button
            variant="primary"
            sx={{ width: theme.spacing(20) }}
            onClick={() => {
              handleOpenDrawer(DrawerType.SEND_ATTACHMENT);
              setSelectedPeriod(null);
            }}
          >
            {t('import.attachment.eight')}
          </Button>
        </Stack>
      </Box>

      <PeriodTable
        rows={data || []}
        setOpenDrawer={handleOpenDrawer}
        setSelectedPeriod={setSelectedPeriod}
        selectedPeriod={selectedPeriod as PeriodsResponse}
        handleSyncSharing={handleSyncSharing}
        handleSyncEmployee={handleSyncEmployee}
        handleProcess={handleProcess}
        isDisabled={isDisabled}
      />

      <LateralDrawer
        open={openDrawer === DrawerType.SEND_ATTACHMENT}
        setOpen={(v) => setOpenDrawer(v ? DrawerType.SEND_ATTACHMENT : DrawerType.NONE)}
        onClose={handleCloseDrawer}
        position="right"
        size="bigger"
      >
        <NewPeriodRequestScreen
          setDrawerSendAttachmentEight={handleCloseDrawer}
          selectedPeriod={selectedPeriod?.date as string}
          monthYear={selectedPeriod?.monthYear as string}
        />
      </LateralDrawer>
      <LateralDrawer
        open={openDrawer === DrawerType.SEND_REASON}
        setOpen={(v) => setOpenDrawer(v ? DrawerType.SEND_REASON : DrawerType.NONE)}
        onClose={handleCloseDrawer}
        position="right"
        size="bigger"
      >
        <NewSendReason
          setDrawerSendAttachmentEight={handleCloseDrawer}
          selectedPeriod={selectedPeriod?.date as string}
          monthYear={selectedPeriod?.monthYear as string}
        />
      </LateralDrawer>
      <LateralDrawer
        open={openDrawer === DrawerType.SEND_TRANSP_SPREADSHEET}
        setOpen={(v) => setOpenDrawer(v ? DrawerType.SEND_TRANSP_SPREADSHEET : DrawerType.NONE)}
        onClose={handleCloseDrawer}
        position="right"
        size="bigger"
      >
        <NewSendTranspSpreadsheet
          setDrawerSendAttachmentEight={handleCloseDrawer}
          selectedPeriod={selectedPeriod?.date as string}
          monthYear={selectedPeriod?.monthYear as string}
        />
      </LateralDrawer>
    </Box>
  );
}
export default Imports;
